import * as v from "valibot";
import { AnswerJsonSchema, OfferJsonSchema } from "../../shared/schemas";
import type { CallView } from "../../shared/types";

type Recovery = NonNullable<CallView["recovery"]>;
type Options = {
  outgoing: boolean;
  available: () => boolean;
  restart: (revision: number) => Promise<void>;
  describe: (
    revision: number,
    description: RTCSessionDescriptionInit,
  ) => Promise<void>;
  refreshIce: () => Promise<RTCIceServer[]>;
  reconnecting: () => void;
  connected: () => void;
  failed: () => void;
};

// The original caller always offers, even when the recipient changes networks.
// Revisions make retries idempotent and keep delayed SDP/ICE out of a newer restart.
export class CallRecovery {
  revision = 0;
  localRevision = 0;
  remoteRevision = 0;
  private disposed = false;
  private recovering = false;
  private requesting = false;
  private complete = true;
  private requestedRevision = 0;
  private local: RTCSessionDescriptionInit | null = null;
  private deadline: ReturnType<typeof setTimeout> | null = null;
  private retry: ReturnType<typeof setInterval> | null = null;
  private disconnected: ReturnType<typeof setTimeout> | null = null;

  private peer: RTCPeerConnection;
  private options: Options;
  constructor(peer: RTCPeerConnection, options: Options) {
    this.peer = peer;
    this.options = options;
    peer.addEventListener("connectionstatechange", this.stateChanged);
    peer.addEventListener("iceconnectionstatechange", this.stateChanged);
  }

  private stateChanged = () => {
    if (this.disposed) return;
    if (
      this.peer.connectionState === "failed" ||
      this.peer.iceConnectionState === "failed"
    ) {
      if (!this.recovering) this.recover();
    } else if (
      this.peer.connectionState === "disconnected" ||
      this.peer.iceConnectionState === "disconnected"
    ) {
      if (!this.recovering && !this.disconnected)
        this.disconnected = setTimeout(() => {
          this.disconnected = null;
          this.recover();
        }, 2000);
    } else if (this.peer.connectionState === "connected" && this.complete) {
      this.clearTimers();
      this.recovering = false;
      this.options.connected();
    }
  };

  private begin(requestedRevision = this.revision) {
    if (this.recovering || this.disposed) return;
    this.recovering = true;
    this.complete = false;
    this.requestedRevision = requestedRevision;
    this.options.reconnecting();
    this.deadline = setTimeout(() => {
      if (!this.disposed) this.options.failed();
    }, 60000);
    this.retry = setInterval(() => void this.attempt(), 8000);
  }

  recover = () => {
    if (this.disposed || this.recovering || !this.peer.remoteDescription)
      return;
    this.begin();
    void this.attempt();
  };

  resume() {
    if (this.recovering) void this.attempt();
    else this.stateChanged();
  }

  private async attempt() {
    if (this.disposed || this.requesting || !this.options.available()) return;
    this.requesting = true;
    try {
      // Re-read/rebroadcast an unfinished exchange instead of replacing its
      // offer before a peer with a broken socket can retrieve it over HTTP.
      await this.options.restart(
        this.complete ? this.revision : this.requestedRevision,
      );
    } catch {
      // A network switch can interrupt this request too. Ready/online events and
      // the bounded retry loop resume it without releasing the media tracks.
    } finally {
      this.requesting = false;
    }
  }

  async apply(value: Recovery | null) {
    if (!value || this.disposed || value.revision < this.revision) return;
    if (value.revision > this.revision) {
      this.revision = value.revision;
      this.local = null;
      this.complete = false;
      this.begin(value.revision - 1);
    }
    if (this.options.outgoing) {
      if (!value.offer) {
        if (!this.local) {
          const iceServers = await this.options.refreshIce();
          if (this.disposed) return;
          this.peer.setConfiguration({
            ...this.peer.getConfiguration(),
            iceServers,
          });
          if (this.peer.signalingState === "have-local-offer")
            await this.peer.setLocalDescription({ type: "rollback" });
          if (this.disposed) return;
          const offer = await this.peer.createOffer({ iceRestart: true });
          if (this.disposed) return;
          this.localRevision = value.revision;
          await this.peer.setLocalDescription(offer);
          if (this.disposed) return;
          this.local = this.peer.localDescription;
        }
        await this.options.describe(value.revision, this.local!);
      } else if (value.answer) {
        if (this.remoteRevision !== value.revision) {
          await this.peer.setRemoteDescription(
            v.parse(AnswerJsonSchema, value.answer),
          );
          if (this.disposed) return;
          this.remoteRevision = value.revision;
        }
        this.complete = true;
      }
    } else if (value.offer) {
      if (this.remoteRevision !== value.revision) {
        const iceServers = await this.options.refreshIce();
        if (this.disposed) return;
        this.peer.setConfiguration({
          ...this.peer.getConfiguration(),
          iceServers,
        });
        await this.peer.setRemoteDescription(
          v.parse(OfferJsonSchema, value.offer),
        );
        if (this.disposed) return;
        this.remoteRevision = value.revision;
      }
      if (!this.local) {
        const answer = await this.peer.createAnswer();
        if (this.disposed) return;
        this.localRevision = value.revision;
        await this.peer.setLocalDescription(answer);
        if (this.disposed) return;
        this.local = this.peer.localDescription;
      }
      if (!value.answer)
        await this.options.describe(value.revision, this.local!);
      if (this.disposed) return;
      this.complete = true;
    }
    this.stateChanged();
  }

  private clearTimers() {
    if (this.deadline) clearTimeout(this.deadline);
    if (this.retry) clearInterval(this.retry);
    if (this.disconnected) clearTimeout(this.disconnected);
    this.deadline = this.retry = this.disconnected = null;
  }

  dispose() {
    this.disposed = true;
    this.clearTimers();
    this.peer.removeEventListener("connectionstatechange", this.stateChanged);
    this.peer.removeEventListener(
      "iceconnectionstatechange",
      this.stateChanged,
    );
  }
}
